import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';
import { forkJoin, Subscription } from 'rxjs';
import { PortalService } from '../../portale/_services/portal.service';
import { ConfigService } from '../../_services/config.service';
import { collapseMenuHubs } from '../../_services/menu-hub';
import { conPadriInTendina, isMenuCategory, senzaVociNascoste } from '../../_services/menu-organization';
import { isMenuUrlActive, menuRouterUrl } from '../app-sidebar-nav/app-sidebar-nav.component';

interface Breadcrumb {
	label: string;
	/** null = gruppo del menu senza pagina: si mostra ma non si clicca */
	url: string | null;
}

@Component({
  standalone: false,
	selector: 'app-breadcrumbs',
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './app-breadcrumb.component.html',
})
export class AppBreadcrumbsComponent implements OnInit, OnDestroy {
	breadcrumbs: Array<Breadcrumb> = [];
	private routerSubscription: Subscription | undefined;
	private menuChangedSub: Subscription | undefined;
	/** Menu come lo mostrano barra in alto e sidebar (organizzazione dell'azienda, sezioni a schede, voci nascoste tolte) */
	private menu: any[] = [];

	constructor(
		private router: Router,
		private activatedRoute: ActivatedRoute,
		private portalservice: PortalService,
		private configService: ConfigService,
		private translate: TranslateService,
	) {}

	ngOnInit(): void {
		this.routerSubscription = this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => this.aggiorna());
		// il menu dell'azienda e' cambiato (salvataggio dell'editor, cambio azienda): il percorso si ricalcola
		this.menuChangedSub = this.portalservice.menuChanged$.subscribe(() => this.caricaMenu());
		this.caricaMenu();
	}

	ngOnDestroy(): void {
		this.routerSubscription?.unsubscribe();
		this.menuChangedSub?.unsubscribe();
	}

	private caricaMenu() {
		if (this.configService.getLocalStorageObjectJson('currentUser') == null) return;
		const prt = this.configService.getPortalID();
		const cmp = this.configService.getCompany_id();
		forkJoin([this.portalservice.getMenuItems(prt, cmp), this.portalservice.getMenuHubs(prt, cmp)]).subscribe(([menuItems, hubs]) => {
			this.menu = conPadriInTendina(senzaVociNascoste(collapseMenuHubs(menuItems || [], hubs, cmp)));
			this.aggiorna();
		});
	}

	/**
	 * Percorso nel menu dell'azienda (gruppo > sottogruppo > voce) della voce che corrisponde meglio alla pagina, come
	 * la evidenziano menu e seconda barra; se la pagina e' piu' dentro (dettaglio, scheda di una sezione) si aggiunge il
	 * suo titolo dalle rotte. Pagina che il menu non raggiunge: il breadcrumb delle rotte, come prima.
	 */
	private aggiorna() {
		const rotte = this.createBreadcrumbs(this.activatedRoute.root);
		const percorso = this.percorsoNelMenu();
		if (!percorso.length) {
			this.breadcrumbs = rotte;
			return;
		}
		const voce = percorso[percorso.length - 1];
		const crumbs: Breadcrumb[] = percorso.map((n) => ({ label: n.pg_title, url: isMenuCategory(n) ? null : menuRouterUrl(n.url) }));
		const ultimaRotta = rotte[rotte.length - 1];
		const stessaPagina = this.norm(this.router.url) === this.norm(voce.url);
		if (!stessaPagina && ultimaRotta && this.testo(ultimaRotta.label) !== this.testo(voce.pg_title)) {
			crumbs.push({ label: ultimaRotta.label, url: ultimaRotta.url });
		}
		this.breadcrumbs = crumbs;
	}

	/** Antenati + voce del menu con l'indirizzo piu' specifico che corrisponde alla pagina corrente. */
	private percorsoNelMenu(): any[] {
		let migliore: any[] = [];
		let lunghezza = 0;
		const visita = (voci: any[], antenati: any[]) => {
			for (const v of voci || []) {
				if (!isMenuCategory(v) && !(v?.escludiUrls || []).some((u: string) => isMenuUrlActive(this.router, u))) {
					const corrispondenze = [v.url, ...(v?.hubUrls || [])].filter((u) => isMenuUrlActive(this.router, u));
					for (const u of corrispondenze) {
						const l = this.norm(u).length;
						if (l > lunghezza) {
							lunghezza = l;
							migliore = [...antenati, v];
						}
					}
				}
				if (v?.submenu?.length) visita(v.submenu, [...antenati, v]);
			}
		};
		visita(this.menu, []);
		return migliore;
	}

	private norm(url: any): string {
		return '/' + String(url || '').split('?')[0].split('#')[0].replace(/^\/+/, '').replace(/\/+$/, '');
	}

	private testo(label: string): string {
		return String(this.translate.instant(label || '')).trim().toLowerCase();
	}

	private createBreadcrumbs(route: ActivatedRoute, url: string = '', breadcrumbs: Breadcrumb[] = []): Breadcrumb[] {
		// Get the child routes
		const children: ActivatedRoute[] = route.children;

		// Return if there are no more children
		if (children.length === 0) {
			return breadcrumbs;
		}

		// Find the primary outlet route
		const primaryOutlet = children.find((child) => child.outlet === 'primary');

		if (!primaryOutlet) {
			return breadcrumbs;
		}

		// Get the route's URL segment
		const routeURL: string = primaryOutlet.snapshot.url.map((segment) => segment.path).join('/');

		// Append route URL to URL
		const currentUrl = routeURL ? `${url}/${routeURL}` : url;

		// Add breadcrumb if the route has data
		if (primaryOutlet.snapshot.data['title']) {
			const breadcrumb: Breadcrumb = {
				label: primaryOutlet.snapshot.data['title'],
				url: currentUrl,
			};
			breadcrumbs.push(breadcrumb);
		}

		// Continue with the next level
		return this.createBreadcrumbs(primaryOutlet, currentUrl, breadcrumbs);
	}
}
